import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { Filter, FilterOperator } from '../../types/analytics.types';
import { getFieldConfig, categoryLabel, categoryColor, OPERATOR_LABELS } from '../../lib/filterConfig';
import { useCatalogStore } from '../../stores/catalogStore';
import { FilterValueInput } from './FilterValueInput';

interface FilterRowProps {
  filter: Filter;
  onUpdate: (updates: Partial<Filter>) => void;
  onRemove: () => void;
}

export function FilterRow({ filter, onUpdate, onRemove }: FilterRowProps) {
  useCatalogStore((s) => s.loaded); // re-render once the catalog arrives
  const fieldConfig = getFieldConfig(filter.category, filter.field);

  if (!fieldConfig) {
    return null;
  }

  const availableOperators = fieldConfig.operators;

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg border bg-card">
      <Badge
        variant="secondary"
        className={`${categoryColor(filter.category)} border-0 whitespace-nowrap`}
      >
        {categoryLabel(filter.category)}
      </Badge>

      <span className="text-sm font-medium whitespace-nowrap">
        {fieldConfig.label}
      </span>

      <Select
        value={filter.operator}
        onValueChange={(value) => onUpdate({ operator: value as FilterOperator })}
      >
        <SelectTrigger className="w-full sm:w-[160px]">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {availableOperators.map((op) => (
            <SelectItem key={op} value={op}>
              {OPERATOR_LABELS[op]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <FilterValueInput
        fieldConfig={fieldConfig}
        operator={filter.operator}
        value={filter.value}
        onChange={(value) => onUpdate({ value })}
      />

      <Button
        variant="ghost"
        size="icon"
        onClick={onRemove}
        className="h-8 w-8 flex-shrink-0 ml-auto"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}
